/**
 * Orders Overlay Manager
 * Manages pending order lines on the TradingView chart
 * Uses createShape() API (Charting Library edition - Trading Platform not available)
 * 
 * Note: createOrderLine() requires Trading Platform edition
 * We use shapes instead, which are available in standard Charting Library
 * 
 * Draggable Feature: Lines can be dragged to modify order prices
 * Uses debouncing to prevent spam - only triggers callback after user stops dragging
 */

import type { StoreOrder } from "@/backend/store";

interface OrderLine {
  shapeId: TradingView.EntityId;
  order: StoreOrder;
  originalPrice: number;
}

export interface OrderModifyCallback {
  (orderId: string, newPrice: number, order: StoreOrder): void;
}

// Debounce delay in milliseconds - wait this long after user stops dragging
const DEBOUNCE_DELAY_MS = 1500;

export class OrdersOverlay {
  private orderLines: Map<string, OrderLine> = new Map();
  private chart: TradingView.IChartWidgetApi | null = null;
  private onModifyCallback: OrderModifyCallback | null = null;
  private checkInterval: ReturnType<typeof setInterval> | null = null;
  private lastKnownPrices: Map<string, number> = new Map();
  
  // Debounce timers per order - only fire callback after user stops dragging
  private debounceTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private pendingPrices: Map<string, number> = new Map();

  /**
   * Set the callback for when an order line is modified (dragged)
   */
  setOnModifyCallback(callback: OrderModifyCallback | null): void {
    this.onModifyCallback = callback;
  }

  /**
   * Start checking for price changes on draggable lines
   */
  private startPriceCheck(): void {
    if (this.checkInterval) return;

    // Check every 300ms for price changes (faster polling, but debounced callback)
    this.checkInterval = setInterval(() => {
      this.checkForPriceChanges();
    }, 300);
  }

  /**
   * Stop checking for price changes
   */
  private stopPriceCheck(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    // Clear all pending debounce timers
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer);
    }
    this.debounceTimers.clear();
    this.pendingPrices.clear();
  }

  /**
   * Check if any shape prices have changed (user dragged them)
   * Uses debouncing to only trigger callback after user stops dragging
   */
  private checkForPriceChanges(): void {
    if (!this.chart || !this.onModifyCallback) return;

    for (const [orderId, orderLine] of this.orderLines.entries()) {
      try {
        // Try to get the shape and its current price
        const shape = this.chart.getShapeById(orderLine.shapeId);
        if (!shape) continue;

        const points = shape.getPoints();
        if (!points || points.length === 0) continue;

        const currentPrice = points[0].price;
        const lastKnownPrice = this.lastKnownPrices.get(orderId) || orderLine.originalPrice;

        // Check if price has changed significantly (more than 0.001% to avoid floating point issues)
        const priceChangePercent = Math.abs((currentPrice - lastKnownPrice) / lastKnownPrice);
        if (priceChangePercent > 0.00001) {
          // Price changed! Update tracking and schedule debounced callback
          this.lastKnownPrices.set(orderId, currentPrice);
          
          // Clear existing debounce timer for this order
          const existingTimer = this.debounceTimers.get(orderId);
          if (existingTimer) {
            clearTimeout(existingTimer);
          }
          
          // Store pending price and schedule debounced callback
          this.pendingPrices.set(orderId, currentPrice);
          
          const timer = setTimeout(() => {
            const pendingPrice = this.pendingPrices.get(orderId);
            if (pendingPrice !== undefined && this.onModifyCallback) {
              console.log(`[OrdersOverlay] Order ${orderId} drag complete: → $${pendingPrice.toFixed(2)}`);
              this.onModifyCallback(orderId, pendingPrice, orderLine.order);
            }
            this.debounceTimers.delete(orderId);
            this.pendingPrices.delete(orderId);
          }, DEBOUNCE_DELAY_MS);
          
          this.debounceTimers.set(orderId, timer);
        }
      } catch (e) {
        // Shape might not exist or API not available
      }
    }
  }

  /**
   * Update order lines on the chart using createShape API
   */
  async updateOrders(
    orders: StoreOrder[],
    currentMarket: string,
    chart: TradingView.IChartWidgetApi
  ): Promise<void> {
    this.chart = chart;

    // Filter orders for current market (only open/pending limit orders)
    const marketOrders = orders.filter(
      (order) =>
        order.market === currentMarket &&
        order.orderType === "limit" &&
        (order.status === "open" || order.status === "pending" || order.status === "partially_filled")
    );

    // Get current order IDs
    const currentOrderIds = new Set(marketOrders.map((o) => o.id));

    // Remove orders that no longer exist
    for (const [orderId, orderLine] of this.orderLines.entries()) {
      if (!currentOrderIds.has(orderId)) {
        try {
          chart.removeEntity(orderLine.shapeId);
        } catch (e) {
          // Entity may already be removed
        }
        this.orderLines.delete(orderId);
        this.lastKnownPrices.delete(orderId);
        // Clear any pending debounce timer
        const timer = this.debounceTimers.get(orderId);
        if (timer) {
          clearTimeout(timer);
          this.debounceTimers.delete(orderId);
        }
        this.pendingPrices.delete(orderId);
      }
    }

    // Get current time for shape creation
    const currentTime = Date.now() / 1000;

    // Add or update orders
    for (const order of marketOrders) {
      const existingLine = this.orderLines.get(order.id);

      if (existingLine) {
        // Check if price changed - if so, recreate the line
        // But only if the backend price changed (not from dragging)
        const backendPriceChanged = Math.abs(order.price - existingLine.originalPrice) > 0.01;
        
        if (backendPriceChanged ||
            existingLine.order.size !== order.size ||
            existingLine.order.filledSize !== order.filledSize) {
          try {
            chart.removeEntity(existingLine.shapeId);
          } catch (e) {
            // Entity may already be removed
          }
          this.orderLines.delete(order.id);
          this.lastKnownPrices.delete(order.id);
          // Will be created below
        } else {
          // No significant change from backend
          continue;
        }
      }

      // Create new order line (DRAGGABLE!)
      const isBuy = order.side === "buy";
      // Green for buy, red for sell (site theme colors with 80% opacity)
      const colorWithOpacity = isBuy ? "rgba(16, 185, 129, 0.8)" : "rgba(246, 70, 93, 0.8)";

      // Calculate remaining size for partial fills
      const remainingSize = order.size - (order.filledSize || 0);
      const sizeDisplay = remainingSize.toFixed(4).replace(/\.?0+$/, "");

      // Format label
      const sideLabel = isBuy ? "BUY" : "SELL";
      const priceDisplay = order.price.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      const label = `${sideLabel} ${sizeDisplay} @ $${priceDisplay}`;

      try {
        const shapeId = await chart.createShape(
          { time: currentTime, price: order.price },
          {
            shape: "horizontal_line",
            lock: false,  // ← DRAGGABLE! User can move the line
            disableSelection: false,
            disableSave: true,
            disableUndo: true,
            overrides: {
              linecolor: colorWithOpacity,
              linewidth: 1,
              linestyle: 2, // Dotted line for pending orders
              showPrice: true,
              textcolor: colorWithOpacity,
              horzLabelsAlign: "left",
              fontsize: 11,
              showLabel: true,
            },
            text: label,
          }
        );

        if (shapeId) {
          this.orderLines.set(order.id, { 
            shapeId, 
            order: { ...order },
            originalPrice: order.price 
          });
          this.lastKnownPrices.set(order.id, order.price);
        }
      } catch (error) {
        console.error("[OrdersOverlay] ❌ Error creating order line:", error);
      }
    }

    // Start price checking if we have orders and a callback
    if (this.orderLines.size > 0 && this.onModifyCallback) {
      this.startPriceCheck();
    } else {
      this.stopPriceCheck();
    }
  }

  /**
   * Get the current price of an order line (after potential drag)
   */
  getOrderPrice(orderId: string): number | null {
    const orderLine = this.orderLines.get(orderId);
    if (!orderLine || !this.chart) return null;

    try {
      const shape = this.chart.getShapeById(orderLine.shapeId);
      if (!shape) return null;

      const points = shape.getPoints();
      if (!points || points.length === 0) return null;

      return points[0].price;
    } catch (e) {
      return null;
    }
  }

  /**
   * Clear all order lines from the chart
   */
  clear(): void {
    this.stopPriceCheck();
    
    if (this.chart) {
      for (const orderLine of this.orderLines.values()) {
        try {
          this.chart.removeEntity(orderLine.shapeId);
        } catch (e) {
          // Entity may already be removed
        }
      }
    }
    this.orderLines.clear();
    this.lastKnownPrices.clear();
    this.chart = null;
  }

  /**
   * Get all current order line IDs
   */
  getOrderIds(): string[] {
    return Array.from(this.orderLines.keys());
  }
}
